/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_TRANSPARENT,
  DROPDOWN_POSITION_LEFT,
  Dropdown,
  DropdownItemData,
  DropdownMenuProps,
  DropdownSectionData,
  ICON_CAPABILITY_HOME,
  ICON_CHEVRON_RIGHT,
  LOGO_DXP_SWITCHER,
  SdsIcon,
} from '@squiz/sds';
import { useConsoleServiceStore, useOverlayStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DXP_CAPABILITY_ITEMS } from '../../Constants/DxpCapabilities/DxpCapabilityItems.constants';
import { CORE_STRINGS } from '../../Constants/Strings.constants';
import { EntityDxpUserMeOrganisation } from '../../Services/console';
import { getOrganisationPath } from '../../Utilities/GetOrganisationPath/GetOrganisationPath';
import { hasPermission } from '../../Utilities/HasPermission/HasPermission';
import { isFeatureEnabled } from '../../Utilities/IsFeatureEnabled/IsFeatureEnabled';
import { replaceTenantParam } from '../../Utilities/ReplaceTenantParam/ReplaceTenantParam';
import { CAPABILITY_SWITCHER_DATA_IDS } from './CapabilitySwitcher.constants';

/**
 * The capability switcher.
 */
export const CapabilitySwitcher = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore();

  // Labels
  const labels = useMemo(() => {
    return {
      dropdownLabel: t(CORE_STRINGS.capabilitySwitcher),
      home: t(CORE_STRINGS.home),
    };
  }, [t]);

  // Capabilities
  const unlockedCapabilities = useMemo(() => {
    return DXP_CAPABILITY_ITEMS.filter((item) => {
      const { featureFlag, permission, subscription } = item;

      // If the capability's subscription type is not found in the organization's
      // subscription list, exclude capability from dropdown
      let hasAccess = true;
      if (subscription && tenant) {
        if (tenant.organisationSubscriptions.indexOf(subscription) === -1) {
          hasAccess = false;
        }
      }
      // If a certain permission level is required but not met,
      // exclude capability from dropdown
      if (permission && !hasPermission(permission)) {
        hasAccess = false;
      }

      // If a feature flag controls access to the capability,
      // but it isn't enabled, exclude capability from dropdown
      if (featureFlag && !isFeatureEnabled(featureFlag)) {
        hasAccess = false;
      }

      return hasAccess;
    });
  }, [tenant]);

  const getDropdownItems = useCallback((): Array<DropdownItemData> => {
    // Home
    const dropdownItemHome: DropdownItemData = {
      action: (): void => {
        navigate({ pathname: getOrganisationPath(``, true) });
        return;
      },
      dataID: CAPABILITY_SWITCHER_DATA_IDS.switcherDropdownItemHome,
      icon: ICON_CAPABILITY_HOME,
      key: `capability-switcher-item--home`,
      label: (
        <>
          <span>{labels.home}</span>
          <SdsIcon svg={ICON_CHEVRON_RIGHT} />
        </>
      ),
    };

    const capabilityDropdownItems: Array<DropdownItemData> = unlockedCapabilities.map((item) => {
      const path = replaceTenantParam(item.path);
      const dataId = CAPABILITY_SWITCHER_DATA_IDS.switcherDropdownItem.replace(`{{capability}}`, item.title);

      return {
        action: (): void => {
          navigate({ pathname: path });
          return;
        },
        dataID: dataId,
        icon: item.icon,
        key: `capability-switcher-item--${item.title}`,
        label: (
          <>
            <span>{item.title}</span>
            <SdsIcon svg={ICON_CHEVRON_RIGHT} />
          </>
        ),
      };
    });

    return [dropdownItemHome, ...capabilityDropdownItems];
  }, [labels.home, navigate, unlockedCapabilities]);

  const dropdownSection: DropdownSectionData = {
    items: getDropdownItems(),
    key: `capability-switcher-section`,
  };

  const dropdownProps: DropdownMenuProps = {
    buttonProps: {
      classes: `sds-toolbar-logo--switcher`,
      format: BUTTON_FORMAT_TRANSPARENT,
      icon: LOGO_DXP_SWITCHER,
      label: labels.dropdownLabel,
    },
    dropdownPosition: DROPDOWN_POSITION_LEFT,
    overlayStateHandler: useOverlayStore,
    sections: [dropdownSection],
    toggleOverlay: { fullScreen: false },
  };

  return (
    <div className="capability-switcher">
      <Dropdown {...dropdownProps} />
    </div>
  );
};
